import { useState } from 'react';
import type { UnlockId } from '@/core/content/ids';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { UpcomingPopover } from './UpcomingPopover';
import { useSave, useServices } from '@/ui/services/ServicesContext';
import { useCountUp, useReducedMotion } from '@/ui/hooks/useCountUp';
import { CoinIcon, HeartIcon, StarIcon } from './Icons';
import styles from './HudStats.module.css';
import { currentRoom } from '@/ui/util/room';

/** Coins, Stars and the room's coziness towards its next level (shown on `best`, plan §3.2). */
export function HudStats({
  onExpand,
  onAllUnlocks,
}: {
  onExpand?: (unlockId: UnlockId) => void;
  onAllUnlocks?: () => void;
}) {
  const { t } = useI18n();
  const { content } = useServices();
  const [upcoming, setUpcoming] = useState(false);
  const animate = !useReducedMotion();
  const coins = useCountUp(
    useSave((s) => s.wallet.coins),
    animate,
  );
  const stars = useCountUp(
    useSave((s) => s.stars.total),
    animate,
  );
  const roomId = useSave(currentRoom);
  // Studio NANO has no Coziness levels: the meter shows its Tiếng lành instead (studio-nano.md §5).
  const studio = roomId === 'studio';
  const best = useSave((s) => (studio ? s.studio.reputation : s.coziness[roomId].best));
  const levels = studio ? content.balance.studio.levels : content.balance.cozinessLevels[roomId];
  const level = levels.filter((v) => v <= best).length;
  const label = studio ? t('ui.hud.studio_label') : t('ui.hud.coziness_label');
  const prev = level === 0 ? 0 : (levels[level - 1] ?? 0);
  const next = levels[level];
  const progress = next === undefined ? 1 : (best - prev) / (next - prev);

  return (
    <div className={styles.row}>
      <span className={styles.chip} data-testid="hud-coins">
        <CoinIcon />
        <span className="visually-hidden">{t('ui.hud.coins_label')}</span>
        {t('ui.hud.coins', { count: coins })}
      </span>
      <button
        type="button"
        className={`${styles.chip} ${styles.button}`}
        data-testid="hud-stars"
        aria-expanded={upcoming}
        onClick={() => {
          setUpcoming((v) => !v);
        }}
      >
        <StarIcon />
        <span className="visually-hidden">{t('ui.hud.stars_label')}</span>
        {t('ui.hud.stars', { count: stars })}
      </button>
      <span
        className={`${styles.chip} ${styles.cozy}`}
        data-testid="hud-coziness"
        data-studio={studio || undefined}
        title={label}
      >
        {studio ? <StarIcon /> : <HeartIcon />}
        <span className={styles.level}>
          {studio
            ? next === undefined
              ? t('ui.hud.studio_max')
              : t('ui.hud.studio_level', { level: level + 1 })
            : next === undefined
              ? t('ui.hud.coziness_max')
              : t('ui.hud.coziness_level', { level: level + 1 })}
        </span>
        <span
          className={styles.meter}
          role="meter"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
        >
          <span className={styles.fill} style={{ width: `${String(Math.round(progress * 100))}%` }} />
        </span>
      </span>
      {upcoming && (
        <UpcomingPopover
          onClose={() => {
            setUpcoming(false);
          }}
          onExpand={(id) => {
            setUpcoming(false);
            onExpand?.(id);
          }}
          {...(onAllUnlocks
            ? {
                onSeeAll: () => {
                  setUpcoming(false);
                  onAllUnlocks();
                },
              }
            : {})}
        />
      )}
    </div>
  );
}
